import { Receipt, Star, Users, Wrench, Hammer, MessageSquareWarning, Cctv, ClipboardList, Activity, Edit3, ArrowRightLeft, LayoutGrid, Video, Lightbulb, PackageSearch, X, LogOut, Settings, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { APP_VERSION } from "@/lib/version";
import { GlobalSearch } from "@/components/GlobalSearch";

const ICONS = {
  receipt: Receipt, star: Star, users: Users, wrench: Wrench, hammer: Hammer,
  "message-square-warning": MessageSquareWarning, cctv: Cctv, activity: Activity, edit: Edit3,
  movement: ArrowRightLeft, grid: LayoutGrid, video: Video, lightbulb: Lightbulb, package: PackageSearch,
};

export default function Sidebar({ reports, active, onSelect, onSearchResult, open, onClose, onOpenSettings, onOpenTrash, trashCount = 0 }) {
  const { logout } = useAuth();
  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-30 lg:hidden" onClick={onClose} data-testid="sidebar-backdrop" />
      )}
      <aside
        className={cn(
          "w-64 shrink-0 bg-gradient-to-b from-[#e8f3fc] via-[#dcebf8] to-[#edf6fd] backdrop-blur-2xl backdrop-saturate-150 text-slate-900 flex flex-col z-40 border-r border-sky-300/60 shadow-[4px_0_28px_rgba(56,189,248,0.14),inset_-1px_0_1px_rgba(255,255,255,0.85),inset_0_1px_1px_rgba(255,255,255,0.95)] relative overflow-hidden",
          "fixed inset-y-0 left-0 transition-transform duration-300 lg:static lg:translate-x-0 lg:h-screen lg:sticky lg:top-0",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        <div className="absolute -top-16 -left-16 w-48 h-48 bg-sky-300/35 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-blue-400/25 rounded-full blur-3xl pointer-events-none" />
        <div className="px-5 py-5 flex items-center gap-3 border-b border-sky-200/70 bg-white/40 backdrop-blur-md relative z-10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center shadow-md shadow-amber-500/20 ring-1 ring-amber-300/50 shrink-0">
            <ClipboardList className="w-5 h-5 text-slate-950 stroke-[2.2]" />
          </div>
          <div className="flex-1">
            <h1 className="text-slate-900 font-extrabold text-sm leading-tight tracking-tight drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]">Operations Suite</h1>
            <p className="text-[11px] font-semibold text-sky-800/80 tracking-wide">Monthly Report Studio</p>
          </div>
          <button className="lg:hidden p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white/60 rounded-lg transition-colors ring-1 ring-transparent hover:ring-sky-300/60" onClick={onClose} data-testid="sidebar-close" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <GlobalSearch onChoose={onSearchResult} />
        <nav className="flex-1 overflow-y-auto pt-3 pb-4 px-3 space-y-1 relative z-10">
          <p className="px-3 pb-2 text-[10px] font-extrabold uppercase tracking-widest text-sky-900/70 drop-shadow-xs">Reports</p>
          {reports.map((r) => {
            const Icon = ICONS[r.icon] || ClipboardList;
            const isActive = r.id === active;
            return (
              <button key={r.id} data-testid={`nav-${r.id}`} onClick={() => onSelect(r.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 active:scale-[0.99] group border
                  ${isActive ? "font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 text-white shadow-md shadow-blue-600/30 border-blue-400/60 ring-1 ring-white/30"
                             : "font-semibold text-slate-700 border-transparent hover:text-slate-950 hover:bg-white/70 hover:border-sky-300/60 hover:shadow-[0_2px_8px_rgba(56,189,248,0.12),inset_0_1px_0_rgba(255,255,255,0.9)]"}`}>
                <Icon className={cn("w-4 h-4 shrink-0", isActive ? "text-sky-100 stroke-[2.2]" : "text-sky-700/80 group-hover:text-sky-950")} />
                <span className="text-left leading-tight">{r.name}</span>
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-3.5 border-t border-sky-200/70 bg-white/40 backdrop-blur-md space-y-1.5 relative z-10">
          <button onClick={onOpenTrash} data-testid="open-trash-btn"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 border border-transparent hover:text-rose-700 hover:bg-rose-50/80 hover:border-rose-300/60 transition-all duration-150 group">
            <Trash2 className="w-4 h-4 shrink-0 text-rose-500/80 group-hover:text-rose-600 transition-colors" />
            <span>Trash</span>
            {trashCount > 0 && (
              <span className="ml-auto min-w-5 rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white"
                data-testid="trash-count-badge">{trashCount}</span>
            )}
          </button>
          <button onClick={onOpenSettings} data-testid="open-settings-btn"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 border border-transparent hover:text-slate-950 hover:bg-white/70 hover:border-sky-300/60 transition-all duration-150 group">
            <Settings className="w-4 h-4 shrink-0 text-sky-700/80 group-hover:text-slate-950 transition-colors" /> Settings
          </button>
          <button onClick={logout} data-testid="logout-btn"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 border border-transparent hover:text-rose-700 hover:bg-rose-50/80 hover:border-rose-300/60 transition-all duration-150 group">
            <LogOut className="w-4 h-4 shrink-0 text-rose-500/80 group-hover:text-rose-600 transition-colors" /> Sign Out
          </button>
          <p className="text-[11px] font-semibold text-sky-900/50 px-3 pt-1 leading-snug tracking-wide" data-testid="app-version">
            Version {APP_VERSION}
          </p>
        </div>
      </aside>
    </>
  );
}
